import {useBootstrapDataStore} from '@ui/bootstrap-data/bootstrap-data-store';
import {themeEl} from '@ui/root-el';
import {useSettings} from '@ui/settings/use-settings';
import {
  ThemeId,
  ThemeSelectorContext,
  ThemeSelectorContextValue,
} from '@ui/themes/theme-selector-context';
import {usePreferredColorScheme} from '@ui/themes/use-preferred-color-scheme';
import {applyThemeToDom} from '@ui/themes/utils/apply-theme-to-dom';
import {useCookie} from '@ui/utils/hooks/use-cookie';
import {applyTenantCustomCss} from '@common/core/apply-tenant-custom-css';
import {useCallback, useEffect, useMemo, useState} from 'react';

const STORAGE_KEY = 'be-active-theme';

interface ThemeProviderProps {
  children: any;
}
export function ThemeProvider({children}: ThemeProviderProps) {
  const settings = useSettings();
  const preferredColorScheme = usePreferredColorScheme();
  const data = useBootstrapDataStore(s => s.data);
  const tenantThemeRaw = useBootstrapDataStore(
    s => (s.data.tenant?.theme as any)?.values,
  );
  const tenantBackground = useBootstrapDataStore(
    s => (s.data.tenant?.theme as any)?.background,
  );
  const tenantBaseTheme = useBootstrapDataStore(
    s => (s.data.tenant?.theme as any)?.base_theme as ThemeId | undefined,
  );
  const tenantCustomCss = useBootstrapDataStore(
    s => (s.data.tenant?.theme as any)?.custom_css as string | undefined,
  );
  const tenantThemeValues = useMemo<Record<string, string> | undefined>(() => {
    if (!tenantThemeRaw) return undefined;
    const entries = Object.entries(tenantThemeRaw).filter(
      ([, value]) => typeof value === 'string' && value.trim() !== '',
    ) as [string, string][];
    return entries.length ? Object.fromEntries(entries) : undefined;
  }, [tenantThemeRaw]);
  const allThemes = data.themes;

  const [cookieThemeId, setSelectedThemeId] = useCookie(STORAGE_KEY);
  const [forcedThemeId, setForceThemeId] = useState<ThemeId | null>(null);
  const selectedThemeId = forcedThemeId ?? cookieThemeId ?? tenantBaseTheme;

  const selectThemeTemporarily = useCallback((themeId: ThemeId | null) => {
    setForceThemeId(themeId);
  }, []);

  const resolveTheme = useCallback(
    (themeId: string | number) => {
      // set theme based on user preference
      if (themeId == 0 || themeId === 'system') {
        return allThemes.find(t =>
          preferredColorScheme === 'light' ? t.default_light : t.default_dark,
        );
      }

      if (themeId === 'light' || themeId === 'dark') {
        return allThemes.find(t => t.default_light === (themeId === 'light'));
      }

      return allThemes.find(t => t.id == themeId);
    },
    [allThemes, preferredColorScheme],
  );

  let selectedThemeBase = selectedThemeId
    ? resolveTheme(selectedThemeId)
    : tenantBaseTheme
      ? resolveTheme(tenantBaseTheme)
      : resolveTheme(settings.themes?.default_id ?? 0);

  if (!selectedThemeBase) {
    selectedThemeBase = allThemes[0]!;
  }

  const selectedTheme = useMemo(() => {
    if (
      !tenantThemeValues ||
      Object.keys(tenantThemeValues).length === 0
    ) {
      return selectedThemeBase;
    }
    return {
      ...selectedThemeBase,
      values: {
        ...selectedThemeBase.values,
        ...tenantThemeValues,
      },
    };
  }, [selectedThemeBase, tenantThemeValues]);

  // if selected theme is different then the one that was set
  // with server render, set new css variables, this will only
  // happen if user has not selected theme manually and default theme is set to "system"
  const themeSignature = JSON.stringify(selectedTheme.values);

  useEffect(() => {
    const currentThemeId = themeEl.dataset.themeId;
    if (
      selectedTheme &&
      (`${selectedTheme.id}` !== `${currentThemeId}` ||
        themeSignature !== themeEl.dataset.themeSignature)
    ) {
      themeEl.dataset.themeSignature = themeSignature;
      applyThemeToDom(selectedTheme);
    }
  }, [selectedTheme, themeSignature]);

  useEffect(() => {
    if (!themeEl) return;
    if (tenantBackground?.image_url) {
      themeEl.style.backgroundImage = `url(${tenantBackground.image_url})`;
      themeEl.style.backgroundSize = tenantBackground.size ?? 'cover';
      themeEl.style.backgroundRepeat = tenantBackground.repeat ?? 'no-repeat';
    } else {
      themeEl.style.removeProperty('background-image');
      themeEl.style.removeProperty('background-size');
      themeEl.style.removeProperty('background-repeat');
    }
  }, [
    tenantBackground?.image_url,
    tenantBackground?.repeat,
    tenantBackground?.size,
  ]);

  useEffect(() => {
    applyTenantCustomCss(tenantCustomCss);
  }, [tenantCustomCss]);

  const contextValue: ThemeSelectorContextValue = useMemo(() => {
    return {
      allThemes,
      selectedTheme: selectedTheme!,
      selectThemeTemporarily,
      selectTheme: id => {
        const theme = resolveTheme(id);
        if (theme) {
          setSelectedThemeId(`${theme.id}`);
          applyThemeToDom(theme);
        }
      },
    };
  }, [
    allThemes,
    selectedTheme,
    setSelectedThemeId,
    resolveTheme,
    selectThemeTemporarily,
  ]);

  return (
    <ThemeSelectorContext.Provider value={contextValue}>
      {children}
    </ThemeSelectorContext.Provider>
  );
}
